class PersonalizationToggle extends HTMLElement {
  constructor() {
    super();
    this.selectedValue = this.dataset.selected || 'no';
    this.modal = this.querySelector('[data-personalization-modal]');
    this.productId = this.dataset.productId;
    this.currentStep = 'type-selection';
    this.personalizationData = {};
    this.imgbbApiKey = '2e6ff87b56464d7dbc6e2f8af405e4b2';

    // Novos atributos para controlar cor e relevo
    this.colorEnabled = this.dataset.enableColor === 'true';
    this.reliefEnabled = this.dataset.enableRelief === 'true';

    // Configuração dos produtos de adicional (taxas extras)
    this.addonProducts = {
      'adicional-r-39': { variantId: null, price: 39 },
      'adicional-r-139': { variantId: null, price: 139 },
      'adicional-r-45': { variantId: null, price: 45 }
    };

    this.init();
  }

  init() {
    this.setupButtons();
    this.setupModal();
    this.setupSteps();
  }

  setupButtons() {
    const buttons = this.querySelectorAll('.personalization-toggle__button');

    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();

        // Remove selected de todos os botões
        buttons.forEach(btn => {
          btn.classList.remove('selected');
          btn.setAttribute('aria-pressed', 'false');
        });

        // Adiciona selected ao botão clicado
        button.classList.add('selected');
        button.setAttribute('aria-pressed', 'true');

        const value = button.dataset.value;
        this.selectedValue = value;

        // Se clicar em SIM, abre o modal
        if (value === 'yes') {
          this.openModal();
        } else {
          // Se clicar em NÃO, limpa todas as properties
          this.clearAllProperties();
        }
      });
    });
  }

  setupModal() {
    // Botão de fechar
    const closeBtn = this.modal.querySelector('[data-close-modal]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeModal());
    }

    // Fechar ao clicar no backdrop
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });
  }

  setupSteps() {
    // Setup para cada step
    this.setupTypeSelection();
    this.setupStyleSelection();
    this.setupColorSelection();
    this.setupBrasaoSelection();
    this.setupInitialsInput();
    this.setupFontSelection();
    this.setupLogoUpload();
    this.setupClicheConfirmation();
    this.setupSummary();

    // Setup dos botões de ação
    this.setupActionButtons();
  }

  setupTypeSelection() {
    const radios = this.modal.querySelectorAll('input[name="personalization_type"]');
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.personalizationData.type = radio.value;
        this.enableNextButton('type-selection');
      });
    });
  }

  setupStyleSelection() {
    const radios = this.modal.querySelectorAll('input[name="personalization_style"]');
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.personalizationData.style = radio.value;

        // Mostra o preço correto baseado no tipo
        this.updatePriceDisplay('style-selection');

        this.enableNextButton('style-selection');
      });
    });
  }

  setupColorSelection() {
    const radios = this.modal.querySelectorAll('input[name="personalization_color"]');
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.personalizationData.color = radio.value;
        this.personalizationData.colorHex = radio.getAttribute('data-hex');
        this.enableNextButton('color-selection');
      });
    });
  }

  setupBrasaoSelection() {
    const radios = this.modal.querySelectorAll('input[name="personalization_brasao"]');
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.personalizationData.brasao = radio.value;

        // Captura também a imagem do brasão selecionado
        const brasaoLabel = radio.closest('.personalization-brasao');
        if (brasaoLabel) {
          const brasaoImg = brasaoLabel.querySelector('img');
          if (brasaoImg) {
            this.personalizationData.brasaoImage = brasaoImg.src;
          }
        }

        this.enableNextButton('brasao-selection');
      });
    });
  }

  setupInitialsInput() {
    const firstInitial = this.modal.querySelector('#first_initial');
    const secondInitial = this.modal.querySelector('#second_initial');

    const validateInitials = () => {
      if (firstInitial.value) {
        this.personalizationData.firstInitial = firstInitial.value.toUpperCase();
        this.enableNextButton('initials-input');
      } else {
        this.disableNextButton('initials-input');
      }

      if (secondInitial.value) {
        this.personalizationData.secondInitial = secondInitial.value.toUpperCase();
      } else {
        delete this.personalizationData.secondInitial;
      }
    };

    if (firstInitial) firstInitial.addEventListener('input', validateInitials);
    if (secondInitial) secondInitial.addEventListener('input', validateInitials);
  }

  setupFontSelection() {
    const radios = this.modal.querySelectorAll('input[name="personalization_font"]');
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.personalizationData.font = radio.value;
        this.enableFinishButton('font-selection');
      });
    });
  }

  setupLogoUpload() {
    const fileInput = this.modal.querySelector('#logo_upload');
    const preview = this.modal.querySelector('.personalization-upload__preview');
    const previewImg = preview?.querySelector('img');
    const removeBtn = preview?.querySelector('[data-action="remove-file"]');
    const uploadLabel = this.modal.querySelector('.personalization-upload__label');

    if (fileInput) {
      fileInput.addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (file) {
          // Mostra preview local
          const reader = new FileReader();
          reader.onload = (e) => {
            if (previewImg) previewImg.src = e.target.result;
            if (preview) preview.style.display = 'block';
            if (uploadLabel) uploadLabel.style.display = 'none';
          };
          reader.readAsDataURL(file);

          // Faz upload para ImgBB
          await this.uploadToImgBB(file);
        }
      });
    }

    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        if (fileInput) fileInput.value = '';
        if (previewImg) previewImg.src = '';
        if (preview) preview.style.display = 'none';
        if (uploadLabel) uploadLabel.style.display = 'flex';
        this.personalizationData.logoUrl = null;
        this.disableFinishButton('logo-upload');
      });
    }
  }

  async uploadToImgBB(file) {
    const uploadLabel = this.modal.querySelector('.personalization-upload__label');
    const finishButton = this.modal.querySelector('[data-step="logo-upload"] [data-action="finish"]');

    try {
      // Mostra loading com spinner
      if (uploadLabel) {
        const originalContent = uploadLabel.innerHTML;
        uploadLabel.innerHTML = `
          <div class="upload-loading">
            <div class="spinner"></div>
            <span>Enviando imagem...</span>
          </div>
        `;

        // Adiciona CSS para o spinner dinamicamente
        if (!document.querySelector('#upload-spinner-styles')) {
          const style = document.createElement('style');
          style.id = 'upload-spinner-styles';
          style.textContent = `
            .upload-loading {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 12px;
            }
            .spinner {
              width: 40px;
              height: 40px;
              border: 4px solid #e0e0e0;
              border-top-color: #af8956;
              border-radius: 50%;
              animation: spin 1s linear infinite;
            }
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `;
          document.head.appendChild(style);
        }
      }

      // Desabilita o botão finalizar durante upload
      if (finishButton) finishButton.disabled = true;

      // Converte arquivo para base64
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => {
          // Remove o prefixo data:image/...;base64,
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Prepara o FormData corretamente conforme a documentação do ImgBB
      const formData = new FormData();
      formData.append('key', this.imgbbApiKey);
      formData.append('image', base64); // Envia o base64 direto
      formData.append('name', file.name.split('.')[0]); // Nome sem extensão

      // Faz a requisição para ImgBB usando FormData
      const response = await fetch('https://api.imgbb.com/1/upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta:', errorText);
        throw new Error(`Erro no upload: ${response.status}`);
      }

      const data = await response.json();
      console.log('Resposta do ImgBB:', data);

      if (data.success) {
        // Armazena a URL da imagem
        this.personalizationData.logoUrl = data.data.url;
        this.enableFinishButton('logo-upload');

        // Mostra mensagem de sucesso
        this.showNotification('Imagem enviada com sucesso!', 'success');

        // Esconde o label de upload pois a preview já está visível
        if (uploadLabel) uploadLabel.style.display = 'none';
      } else {
        throw new Error(data.error?.message || 'Upload falhou');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      this.showNotification('Erro ao enviar imagem. Tente novamente.', 'error');

      // Restaura o label de upload
      if (uploadLabel) {
        uploadLabel.style.display = 'flex';
        uploadLabel.innerHTML = `
          <span class="personalization-upload__icon">
            ${document.querySelector('.personalization-upload__icon')?.innerHTML || '📁'}
          </span>
          <span>Clique para enviar ou arraste o arquivo aqui</span>
          <input 
            type="file" 
            id="logo_upload" 
            name="logo_upload" 
            accept="image/*"
            class="personalization-upload__input"
          />
        `;

        // Reanexa o event listener
        const newFileInput = uploadLabel.querySelector('#logo_upload');
        if (newFileInput) {
          newFileInput.addEventListener('change', async (e) => {
            const file = e.target.files[0];
            if (file) {
              // Mostra preview local
              const preview = this.modal.querySelector('.personalization-upload__preview');
              const previewImg = preview?.querySelector('img');

              const reader = new FileReader();
              reader.onload = (e) => {
                if (previewImg) previewImg.src = e.target.result;
                if (preview) preview.style.display = 'block';
                if (uploadLabel) uploadLabel.style.display = 'none';
              };
              reader.readAsDataURL(file);

              // Faz upload para ImgBB
              await this.uploadToImgBB(file);
            }
          });
        }
      }

      // Reabilita o botão em caso de erro
      if (finishButton) finishButton.disabled = false;
    }
  }

  setupClicheConfirmation() {
    // Habilita o botão de confirmar quando chegar neste step
    const step = this.modal.querySelector('[data-step="cliche-confirmation"]');
    if (step) {
      const finishBtn = step.querySelector('[data-action="finish"]');
      if (finishBtn) {
        // Remove o disabled do botão de confirmar para clichê
        finishBtn.disabled = false;
      }
    }
  }

  setupSummary() {
    // O resumo será preenchido dinamicamente quando chegar neste step
  }

  setupActionButtons() {
    const actionButtons = this.modal.querySelectorAll('[data-action]');

    actionButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const action = button.dataset.action;

        switch (action) {
          case 'next':
            this.nextStep();
            break;
          case 'back':
            this.previousStep();
            break;
          case 'finish':
            this.finishPersonalization();
            break;
          case 'edit':
            this.editPersonalization();
            break;
          case 'add-to-cart':
            this.addToCart();
            break;
        }
      });
    });
  }

  nextStep() {
    const currentStepElement = this.modal.querySelector(`[data-step="${this.currentStep}"]`);
    currentStepElement.style.display = 'none';

    // Determina o próximo step baseado no atual e nas escolhas
    let nextStep = this.getNextStep();

    const nextStepElement = this.modal.querySelector(`[data-step="${nextStep}"]`);
    if (nextStepElement) {
      // Garante que nenhuma opção venha pré-selecionada no próximo step
      const radios = nextStepElement.querySelectorAll('input[type="radio"]');
      radios.forEach(radio => radio.checked = false);

      // Desabilita o botão next/finish do próximo step (exceto para clichê)
      if (nextStep !== 'cliche-confirmation') {
        const nextButton = nextStepElement.querySelector('[data-action="next"], [data-action="finish"]');
        if (nextButton) nextButton.disabled = true;
      }

      nextStepElement.style.display = 'block';
      this.currentStep = nextStep;

      // Se for style-selection, atualiza a exibição de preços
      if (nextStep === 'style-selection') {
        this.updatePriceDisplay('style-selection');
      }

      // Se for cliche-confirmation, habilita o botão de confirmar
      if (nextStep === 'cliche-confirmation') {
        const finishBtn = nextStepElement.querySelector('[data-action="finish"]');
        if (finishBtn) finishBtn.disabled = false;
      }

      // Se for o step de resumo, gera o resumo
      if (nextStep === 'summary') {
        this.showSummary();
      }
    }
  }

  previousStep() {
    const currentStepElement = this.modal.querySelector(`[data-step="${this.currentStep}"]`);
    currentStepElement.style.display = 'none';

    // Remove a property do step atual ao voltar
    this.clearCurrentStepProperty();

    // Desmarca os inputs do step atual
    this.uncheckCurrentStepInputs();

    let previousStep = this.getPreviousStep();

    const previousStepElement = this.modal.querySelector(`[data-step="${previousStep}"]`);
    if (previousStepElement) {
      previousStepElement.style.display = 'block';
      this.currentStep = previousStep;
    }
  }

  uncheckCurrentStepInputs() {
    const currentStepElement = this.modal.querySelector(`[data-step="${this.currentStep}"]`);
    if (currentStepElement) {
      // Desmarca radio buttons
      const radios = currentStepElement.querySelectorAll('input[type="radio"]:checked');
      radios.forEach(radio => radio.checked = false);

      // Limpa text inputs
      const textInputs = currentStepElement.querySelectorAll('input[type="text"]');
      textInputs.forEach(input => input.value = '');

      // Limpa file inputs
      const fileInputs = currentStepElement.querySelectorAll('input[type="file"]');
      fileInputs.forEach(input => input.value = '');

      // Desabilita botões next/finish do step atual
      const actionButtons = currentStepElement.querySelectorAll('[data-action="next"], [data-action="finish"]');
      actionButtons.forEach(btn => btn.disabled = true);
    }
  }

  clearCurrentStepProperty() {
    switch (this.currentStep) {
      case 'style-selection':
        delete this.personalizationData.style;
        break;
      case 'color-selection':
        delete this.personalizationData.color;
        delete this.personalizationData.colorHex;
        break;
      case 'brasao-selection':
        delete this.personalizationData.brasao;
        delete this.personalizationData.brasaoImage;
        break;
      case 'initials-input':
        delete this.personalizationData.firstInitial;
        delete this.personalizationData.secondInitial;
        break;
      case 'font-selection':
        delete this.personalizationData.font;
        break;
      case 'logo-upload':
        delete this.personalizationData.logoUrl;
        // Também limpa a preview
        const preview = this.modal.querySelector('.personalization-upload__preview');
        const uploadLabel = this.modal.querySelector('.personalization-upload__label');
        if (preview) preview.style.display = 'none';
        if (uploadLabel) uploadLabel.style.display = 'flex';
        break;
    }
  }

  getNextStep() {
    const stepFlow = {
      'type-selection': () => {
        if (this.personalizationData.type === 'cliche') {
          return 'cliche-confirmation';
        }

        // NOVA LÓGICA: Verifica os controladores de cor e relevo
        if (this.colorEnabled && !this.reliefEnabled) {
          // Apenas cor habilitada - pula direto para seleção de cor
          this.personalizationData.style = 'colorful'; // Define automaticamente como colorido
          return 'color-selection';
        } else if (!this.colorEnabled && this.reliefEnabled) {
          // Apenas relevo habilitado - pula direto para o próximo step específico do tipo
          this.personalizationData.style = 'relief'; // Define automaticamente como relevo
          if (this.personalizationData.type === 'coat_of_arms') {
            return 'color-selection';
          }
          if (this.personalizationData.type === 'initials') {
            return 'color-selection';
          }
          if (this.personalizationData.type === 'logo') {
            return 'logo-upload';
          }
        } else {
          // Ambos habilitados - mostra seleção de estilo
          return 'style-selection';
        }
      },
      'style-selection': () => {
        // Se for colorido, SEMPRE passa pela seleção de cor primeiro
        if (this.personalizationData.style === 'colorful') {
          return 'color-selection';
        }

        // Se for relevo, depende do tipo
        if (this.personalizationData.type === 'coat_of_arms') {
          return 'color-selection';
        }
        if (this.personalizationData.type === 'initials') {
          return 'color-selection';
        }
        if (this.personalizationData.type === 'logo') {
          return 'logo-upload';
        }
      },
      'color-selection': () => {
        // Após escolher a cor, vai para o step específico do tipo
        if (this.personalizationData.type === 'coat_of_arms') {
          return 'brasao-selection';
        }
        if (this.personalizationData.type === 'initials') {
          return 'initials-input';
        }
        if (this.personalizationData.type === 'logo') {
          return 'logo-upload';
        }
      },
      'brasao-selection': 'initials-input',
      'initials-input': 'font-selection',
      'font-selection': 'summary',
      'logo-upload': 'summary',
      'cliche-confirmation': 'summary'
    };

    const next = stepFlow[this.currentStep];
    return typeof next === 'function' ? next() : next;
  }

  getPreviousStep() {
    const stepFlow = {
      'style-selection': 'type-selection',
      'color-selection': () => {
        // Se pulou o style-selection (apenas cor habilitada ou apenas relevo), volta para type-selection
        if (this.colorEnabled && !this.reliefEnabled) {
          return 'type-selection';
        }
        if (!this.colorEnabled && this.reliefEnabled) {
          return 'type-selection';
        }
        // Senão volta para style-selection
        return 'style-selection';
      },
      'brasao-selection': () => {
        return 'color-selection';
      },
      'initials-input': () => {
        // Se for brasão, volta para seleção de brasão
        if (this.personalizationData.type === 'coat_of_arms') {
          return 'brasao-selection';
        }

        return 'color-selection';
      },
      'font-selection': 'initials-input',
      'logo-upload': () => {
        // Se tem cor selecionada, volta para cor
        if (this.personalizationData.color) {
          return 'color-selection';
        }
        // Se pulou o style-selection (apenas relevo habilitado), volta para type-selection
        if (!this.colorEnabled && this.reliefEnabled) {
          return 'type-selection';
        }
        // Senão volta para estilo
        return 'style-selection';
      },
      'cliche-confirmation': 'type-selection',
      'summary': () => {
        if (this.personalizationData.type === 'cliche') {
          return 'cliche-confirmation';
        }
        if (this.personalizationData.type === 'logo') {
          return 'logo-upload';
        }
        if (this.personalizationData.type === 'initials') {
          return 'font-selection';
        }
        if (this.personalizationData.type === 'coat_of_arms') {
          return 'brasao-selection';
        }
      }
    };

    const previous = stepFlow[this.currentStep];
    return typeof previous === 'function' ? previous() : previous;
  }

  finishPersonalization() {
    // Vai para o resumo
    const currentStepElement = this.modal.querySelector(`[data-step="${this.currentStep}"]`);
    currentStepElement.style.display = 'none';

    this.showSummary();

    const summaryStep = this.modal.querySelector('[data-step="summary"]');
    summaryStep.style.display = 'block';
    this.currentStep = 'summary';
  }

  showSummary() {
    const summaryContainer = this.modal.querySelector('.personalization-summary');
    const totalValue = this.modal.querySelector('.personalization-total__value');

    let summaryHTML = '<dl class="personalization-summary__list">';

    // Tipo de personalização
    summaryHTML += `<dt>Tipo:</dt><dd>${this.getTypeLabel(this.personalizationData.type)}</dd>`;

    // Estilo - SEMPRE mostra mesmo se pulou a seleção
    if (this.personalizationData.style) {
      const styleLabel = this.personalizationData.style === 'colorful' ? 'Colorido' : 'Em Relevo';
      summaryHTML += `<dt>Estilo:</dt><dd>${styleLabel}</dd>`;
    }

    // Cor
    if (this.personalizationData.color) {
      const colorHex = this.personalizationData.colorHex || this.personalizationData.color;
      summaryHTML += `<dt>Cor:</dt><dd><span style="display: inline-block; width: 20px; height: 20px; background-color: ${colorHex}; border: 1px solid #ccc; border-radius: 50%; vertical-align: middle;"></span> ${this.personalizationData.color}</dd>`;
    }

    // Brasão - AGORA COM IMAGEM
    if (this.personalizationData.brasao) {
      summaryHTML += `<dt>Brasão:</dt><dd>${this.personalizationData.brasao}</dd>`;

      // Adiciona a imagem do brasão se existir
      if (this.personalizationData.brasaoImage) {
        summaryHTML += `<dt></dt><dd><img src="${this.personalizationData.brasaoImage}" style="height: 60px; width: auto; object-fit: contain; border: 1px solid #e0e0e0; border-radius: 4px; padding: 4px;"></dd>`;
      }
    }

    // Iniciais
    if (this.personalizationData.firstInitial) {
      let initials = this.personalizationData.firstInitial;
      if (this.personalizationData.secondInitial) {
        initials += this.personalizationData.secondInitial;
      }
      summaryHTML += `<dt>Iniciais:</dt><dd>${initials}</dd>`;
    }

    // Fonte
    if (this.personalizationData.font) {
      summaryHTML += `<dt>Fonte:</dt><dd>${this.personalizationData.font}</dd>`;
    }

    // Logo com altura de 80px
    if (this.personalizationData.logoUrl) {
      summaryHTML += `<dt>Logo:</dt><dd><img src="${this.personalizationData.logoUrl}" style="height: 80px; width: auto; object-fit: contain;"></dd>`;
      summaryHTML += `<dt>Imagem:</dt><dd style="word-break: break-all; font-size: 12px;">${this.personalizationData.logoUrl}</dd>`;
    }

    summaryHTML += '</dl>';

    if (summaryContainer) {
      summaryContainer.innerHTML = summaryHTML;
    }

    // Por enquanto, não mostra valor adicional (será tratado como produto separado)
    if (totalValue) {
      totalValue.style.display = 'none';
    }
    const totalSection = this.modal.querySelector('.personalization-total');
    if (totalSection) {
      totalSection.style.display = 'none';
    }
  }

  editPersonalization() {
    // Limpa TODAS as properties e dados
    this.clearAllProperties();
    this.personalizationData = {};

    // Limpa o resumo visual
    const summaryContainer = this.modal.querySelector('.personalization-summary');
    if (summaryContainer) {
      summaryContainer.innerHTML = '';
    }

    // Esconde o resumo e mostra o primeiro step
    const summaryStep = this.modal.querySelector('[data-step="summary"]');
    summaryStep.style.display = 'none';

    const firstStep = this.modal.querySelector('[data-step="type-selection"]');
    firstStep.style.display = 'block';
    this.currentStep = 'type-selection';

    // Reseta TODOS os inputs
    this.resetAllInputs();
  }

  resetAllInputs() {
    // Reseta todos os radio buttons
    const radios = this.modal.querySelectorAll('input[type="radio"]');
    radios.forEach(radio => radio.checked = false);

    // Reseta inputs de texto
    const textInputs = this.modal.querySelectorAll('input[type="text"]');
    textInputs.forEach(input => input.value = '');

    // Reseta file input
    const fileInput = this.modal.querySelector('#logo_upload');
    if (fileInput) fileInput.value = '';

    // Esconde preview de imagem
    const preview = this.modal.querySelector('.personalization-upload__preview');
    const uploadLabel = this.modal.querySelector('.personalization-upload__label');
    if (preview) preview.style.display = 'none';
    if (uploadLabel) uploadLabel.style.display = 'flex';

    // Desabilita todos os botões next/finish
    const nextButtons = this.modal.querySelectorAll('[data-action="next"], [data-action="finish"]');
    nextButtons.forEach(btn => btn.disabled = true);
  }

  async addToCart() {
    try {
      // Desabilita o botão para evitar múltiplos cliques
      const addButton = this.modal.querySelector('[data-action="add-to-cart"]');
      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = 'Adicionando...';
      }

      // Pega o formulário do produto para obter o variant ID e quantidade
      const productForm = document.querySelector('form[action*="/cart/add"]');
      let variantId = null;
      let quantity = 1;

      if (productForm) {
        // Tenta pegar o variant selecionado
        const variantInput = productForm.querySelector('[name="id"]');
        if (variantInput) {
          variantId = parseInt(variantInput.value);
        }

        // Pega a quantidade
        const quantityInput = productForm.querySelector('[name="quantity"]');
        if (quantityInput) {
          quantity = parseInt(quantityInput.value) || 1;
        }
      }

      // Se não encontrou o variant ID, tenta de outras formas
      if (!variantId) {
        // Tenta pegar do select de variantes
        const variantSelect = document.querySelector('select[name="id"], input[type="radio"][name="id"]:checked');
        if (variantSelect) {
          variantId = parseInt(variantSelect.value);
        }
      }

      if (!variantId) {
        throw new Error('Não foi possível identificar o produto');
      }

      // Monta a property Detalhes com TODAS as informações
      let detalhes = [];

      // Tipo
      if (this.personalizationData.type) {
        detalhes.push(this.getTypeLabel(this.personalizationData.type));
      }

      // Estilo - SEMPRE adiciona se existir
      if (this.personalizationData.style) {
        const styleLabel = this.personalizationData.style === 'colorful' ? 'Colorido' : 'Em Relevo';
        detalhes.push(styleLabel);
      }

      // Cor (incluindo o código hex)
      if (this.personalizationData.color) {
        detalhes.push(`Cor: ${this.personalizationData.color}`);
      }

      // Iniciais
      if (this.personalizationData.firstInitial) {
        let initials = 'Iniciais: ' + this.personalizationData.firstInitial;
        if (this.personalizationData.secondInitial) {
          initials += this.personalizationData.secondInitial;
        }
        detalhes.push(initials);
      }

      // Brasão (sem incluir a URL da imagem para não mostrar ao cliente)
      if (this.personalizationData.brasao) {
        detalhes.push(`Brasão: ${this.personalizationData.brasao}`);
      }

      // Fonte
      if (this.personalizationData.font) {
        detalhes.push(`Fonte: ${this.personalizationData.font}`);
      }

      // URL da Logo
      if (this.personalizationData.logoUrl) {
        detalhes.push(`Imagem: ${this.personalizationData.logoUrl}`);
      }

      // Junta todos os detalhes
      const detalhesString = detalhes.join(' | ');

      // Prepara o item principal (pai)
      const items = [
        {
          id: variantId,
          quantity: quantity,
          properties: {
            'Personalizado': 'Sim',
            'Detalhes': detalhesString
          }
        }
      ];

      // Verifica se precisa adicionar produto de adicional (filho)
      const addonInfo = this.getAddonInfo();

      if (addonInfo) {
        console.log(`Adicional necessário: ${addonInfo.handle} (R$ ${addonInfo.price})`);

        // Busca o variant ID do produto de adicional
        const addonVariantId = await this.fetchAddonVariantId(addonInfo.handle);

        if (addonVariantId) {
          // Adiciona o produto filho (nested item) usando parent_id
          items.push({
            id: addonVariantId,
            quantity: 1,
            parent_id: variantId, // Vincula ao produto pai
            properties: {
              '_adicional': 'true' // Property oculta para identificar
            }
          });

          console.log(`Adicionando nested item: variant ${addonVariantId} vinculado ao pai ${variantId}`);
        } else {
          console.warn(`Não foi possível encontrar o produto de adicional: ${addonInfo.handle}`);
        }
      }

      console.log('Enviando items ao carrinho:', items);

      // Faz a requisição AJAX para adicionar ao carrinho usando JSON (necessário para nested items)
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Erro ao adicionar ao carrinho');
      }

      const data = await response.json();
      console.log('Produtos adicionados com sucesso:', data);

      // Fecha o modal
      this.closeModal();

      // Mostra notificação de sucesso
      this.showNotification('Produto personalizado adicionado ao carrinho!', 'success');

      // Aguarda um pouco e redireciona para o carrinho
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1500);

    } catch (error) {
      console.error('Erro ao adicionar ao carrinho:', error);
      this.showNotification(error.message || 'Erro ao adicionar ao carrinho. Tente novamente.', 'error');

      // Reabilita o botão em caso de erro
      const addButton = this.modal.querySelector('[data-action="add-to-cart"]');
      if (addButton) {
        addButton.disabled = false;
        addButton.textContent = 'Adicionar ao carrinho';
      }
    }
  }

  getTypeLabel(type) {
    const labels = {
      'initials': 'Iniciais',
      'coat_of_arms': 'Brasões',
      'logo': 'Logo',
      'cliche': 'Clichê'
    };
    return labels[type] || type;
  }

  /**
   * Determina qual produto de adicional usar baseado no tipo e estilo selecionados
   * @returns {Object|null} { handle: string, price: number } ou null se não tiver adicional
   */
  getAddonInfo() {
    const { type, style } = this.personalizationData;



    // Demais casos: sem adicional
    return null;
  }

  /**
   * Busca o variant ID de um produto de adicional pelo handle
   * @param {string} handle - Handle do produto (ex: 'adicional-r-39')
   * @returns {Promise<number|null>} Variant ID ou null se não encontrar
   */
  async fetchAddonVariantId(handle) {
    // Verifica se já temos o variant ID em cache
    if (this.addonProducts[handle]?.variantId) {
      return this.addonProducts[handle].variantId;
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        console.error(`Produto de adicional não encontrado: ${handle}`);
        return null;
      }

      const product = await response.json();

      // Pega o primeiro variant disponível
      if (product.variants && product.variants.length > 0) {
        const variantId = product.variants[0].id;

        // Cacheia para uso futuro
        if (this.addonProducts[handle]) {
          this.addonProducts[handle].variantId = variantId;
        }

        return variantId;
      }

      return null;
    } catch (error) {
      console.error(`Erro ao buscar produto de adicional: ${handle}`, error);
      return null;
    }
  }

  updateProperty(name, value) {
    const input = this.querySelector(`input[name="properties[${name}]"]`);
    if (input) {
      input.value = value;
    }
  }

  clearAllProperties() {
    const propertyInputs = this.querySelectorAll('input[name^="properties["]');
    propertyInputs.forEach(input => {
      input.value = '';
    });
  }

  updatePriceDisplay(stepName) {
    const step = this.modal.querySelector(`[data-step="${stepName}"]`);
    const priceElements = step.querySelectorAll('.personalization-option__price');

    priceElements.forEach(element => {
      const type = element.dataset.type;
      if (type === this.personalizationData.type) {
        element.style.display = 'inline';
      } else {
        element.style.display = 'none';
      }
    });
  }

  enableNextButton(stepName) {
    const step = this.modal.querySelector(`[data-step="${stepName}"]`);
    const nextButton = step?.querySelector('[data-action="next"]');
    if (nextButton) {
      nextButton.disabled = false;
    }
  }

  disableNextButton(stepName) {
    const step = this.modal.querySelector(`[data-step="${stepName}"]`);
    const nextButton = step?.querySelector('[data-action="next"]');
    if (nextButton) {
      nextButton.disabled = true;
    }
  }

  enableFinishButton(stepName) {
    const step = this.modal.querySelector(`[data-step="${stepName}"]`);
    const finishButton = step?.querySelector('[data-action="finish"]');
    if (finishButton) {
      finishButton.disabled = false;
    }
  }

  disableFinishButton(stepName) {
    const step = this.modal.querySelector(`[data-step="${stepName}"]`);
    const finishButton = step?.querySelector('[data-action="finish"]');
    if (finishButton) {
      finishButton.disabled = true;
    }
  }

  openModal() {
    if (this.modal) {
      this.modal.showModal();
      document.body.classList.add('scroll-lock');
    }
  }

  closeModal() {
    if (this.modal) {
      // Volta o botão para NÃO
      this.setPersonalizationToNo();

      this.modal.classList.add('dialog-closing');
      setTimeout(() => {
        this.modal.close();
        this.modal.classList.remove('dialog-closing');
        document.body.classList.remove('scroll-lock');
      }, 300);
    }
  }

  setPersonalizationToNo() {
    // Encontra os botões de personalização
    const buttons = this.querySelectorAll('.personalization-toggle__button');
    const noButton = this.querySelector('.personalization-toggle__button[data-value="no"]');

    // Remove selected de todos
    buttons.forEach(btn => {
      btn.classList.remove('selected');
      btn.setAttribute('aria-pressed', 'false');
    });

    // Adiciona selected ao NÃO
    if (noButton) {
      noButton.classList.add('selected');
      noButton.setAttribute('aria-pressed', 'true');
    }

    // Atualiza o valor selecionado
    this.selectedValue = 'no';

    // Limpa todas as properties
    this.clearAllProperties();
  }

  showNotification(message, type = 'info') {
    // Cria um elemento de notificação temporário
    const notification = document.createElement('div');
    notification.className = `personalization-notification personalization-notification--${type}`;
    notification.textContent = message;

    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      background: ${type === 'success' ? '#4caf50' : type === 'error' ? '#f44336' : '#2196f3'};
      color: white;
      border-radius: 4px;
      z-index: 10000;
      animation: slideInRight 0.3s ease;
      max-width: 300px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, type === 'error' ? 5000 : 3000); // Erros ficam mais tempo visíveis
  }
}

// Registra o custom element
customElements.define('personalization-toggle', PersonalizationToggle);